/**
 * Modal for editing manual measurement value, test kit, and notes.
 * Only shown for rows where source === 'manual'.
 */
import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  NumberInput,
  Select,
  Textarea,
  Button,
  Group,
  Text,
  Alert,
  rem,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconEdit, IconCheck, IconX, IconClock } from '@tabler/icons-react';
import {
  useUpdateManualMeasurement,
  TESTER_BRANDS,
  type TesterBrand,
} from '@/api/measurements';
import type { DataPoint } from '@/types/dataEditor';

interface EditManualMeasurementModalProps {
  opened: boolean;
  onClose: () => void;
  dataPoint: DataPoint | null;
  tankId: string;
  parameterKey: string;
}

export function EditManualMeasurementModal({
  opened,
  onClose,
  dataPoint,
  tankId,
  parameterKey,
}: EditManualMeasurementModalProps) {
  const [value, setValue] = useState<number | ''>('');
  const [timestamp, setTimestamp] = useState<Date>(new Date());
  const [tester, setTester] = useState<TesterBrand | null>(null);
  const [note, setNote] = useState<string>('');

  const updateMutation = useUpdateManualMeasurement(tankId);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (opened && dataPoint) {
      setValue(dataPoint.value);
      setTimestamp(new Date(dataPoint.timestamp));
      setTester((dataPoint.tester_brand as TesterBrand) || null);
      setNote(dataPoint.note || '');
    }
  }, [opened, dataPoint]);

  const handleSave = async () => {
    if (!dataPoint?.measurement_id || value === '') return;

    // Determine tester changes
    const originalTester = dataPoint.tester_brand || null;
    const newTester = tester;
    const originalNote = dataPoint.note || '';

    // Build update payload
    const update: {
      value: number;
      recorded_at?: string;
      tester_brand?: TesterBrand;
      clear_tester?: boolean;
      note?: string;
    } = {
      value: typeof value === 'number' ? value : parseFloat(String(value)),
    };

    // Handle timestamp changes
    const originalTimestamp = dataPoint ? new Date(dataPoint.timestamp).getTime() : 0;
    if (timestamp.getTime() !== originalTimestamp) {
      update.recorded_at = timestamp.toISOString();
    }

    // Handle tester changes
    if (newTester !== originalTester) {
      if (newTester === null && originalTester !== null) {
        // User cleared the tester - send clear_tester flag
        update.clear_tester = true;
      } else if (newTester !== null) {
        // User set a new tester
        update.tester_brand = newTester;
      }
    }

    // Handle note changes
    if (note !== originalNote) {
      update.note = note;  // Send new note (empty string clears it)
    }

    try {
      await updateMutation.mutateAsync({
        measurementId: dataPoint.measurement_id,
        update,
      });
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const hasChanges =
    dataPoint &&
    (value !== dataPoint.value ||
      timestamp.getTime() !== new Date(dataPoint.timestamp).getTime() ||
      tester !== (dataPoint.tester_brand || null) ||
      note !== (dataPoint.note || ''));

  // Format precision based on parameter
  const getPrecision = () => {
    if (parameterKey === 'po4') return 3;
    if (['ca', 'mg'].includes(parameterKey)) return 0;
    return 2;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconEdit size={20} />
          <Text fw={600}>Edit Manual Entry</Text>
        </Group>
      }
      size="sm"
      centered
    >
      <Stack gap="md">
        {updateMutation.error && (
          <Alert icon={<IconX size={16} />} color="red" title="Error">
            {updateMutation.error.message}
          </Alert>
        )}

        <DateTimePicker
          label="Date & Time"
          value={timestamp}
          onChange={(val) => val && setTimestamp(new Date(val))}
          leftSection={<IconClock size={16} />}
          valueFormat="MMM D, YYYY h:mm A"
          clearable={false}
          maxDate={new Date()}
          styles={{
            input: {
              fontSize: rem(16),
            },
          }}
        />

        <NumberInput
          label="Value"
          value={value}
          onChange={(val) => setValue(val === '' ? '' : Number(val))}
          decimalScale={getPrecision()}
          min={0}
          required
        />

        <Select
          label="Test Kit"
          placeholder="Select test kit (optional)"
          data={TESTER_BRANDS}
          value={tester}
          onChange={(val) => setTester(val as TesterBrand | null)}
          clearable
        />

        <Textarea
          label="Notes"
          placeholder="Add notes (optional)"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          minRows={2}
          maxRows={4}
          autosize
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={updateMutation.isPending}
            disabled={!hasChanges || value === ''}
            leftSection={<IconCheck size={18} />}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
